import { useState, useSyncExternalStore } from 'react';

export const IMPERSONATE_KEY = 'fd_okta-impersonating-id';

export interface ImpersonationUser {
  id?: string | number;
  oktaUserId?: string;
  [key: string]: unknown;
}

interface ImpersonationStorageRecord {
  oktaUserId: string;
  id?: string | number;
  redirectTo?: string;
}

interface ImpersonateOptions {
  skipRedirect?: boolean;
}

export interface UseImpersonationResult {
  impersonator: ImpersonationUser | null;
  impersonatingUser: ImpersonationUser | null;
  isImpersonating: () => false | ImpersonationStorageRecord;
  impersonate: (
    user: ImpersonationUser,
    redirectTo: string,
    options?: ImpersonateOptions,
  ) => void;
  stopImpersonating: (redirectTo?: string) => void;
}

function getStoredImpersonation(): ImpersonationStorageRecord | null {
  const raw = window.localStorage.getItem(IMPERSONATE_KEY);

  if (!raw) return null;

  try {
    return JSON.parse(raw) as ImpersonationStorageRecord;
  } catch {
    return null;
  }
}

function subscribeToStorage(callback: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === IMPERSONATE_KEY) callback();
  };

  window.addEventListener('storage', handler);

  return () => window.removeEventListener('storage', handler);
}

function getSnapshot(): string | null {
  return window.localStorage.getItem(IMPERSONATE_KEY);
}

export const useImpersonation = (): UseImpersonationResult => {
  const [impersonatingUser, setImpersonatingUser] =
    useState<ImpersonationUser | null>(null);
  const [impersonator, setImpersonator] =
    useState<ImpersonationUser | null>(null);

  useSyncExternalStore(subscribeToStorage, getSnapshot);

  return {
    impersonator,
    impersonatingUser,

    isImpersonating: () => {
      const stored = getStoredImpersonation();

      if (!stored) return false;

      return stored;
    },

    impersonate: (user, redirectTo, { skipRedirect = false } = {}) => {
      if (!user.oktaUserId) return;

      window.localStorage.setItem(
        IMPERSONATE_KEY,
        JSON.stringify({
          oktaUserId: user.oktaUserId,
          id: user.id,
          redirectTo,
        }),
      );

      setImpersonator(null);
      setImpersonatingUser(user);

      if (!skipRedirect) {
        window.location.href = '/home';
      }
    },

    stopImpersonating: (redirectTo) => {
      const stored = getStoredImpersonation();

      window.localStorage.removeItem(IMPERSONATE_KEY);

      window.location.href =
        redirectTo || stored?.redirectTo || '/';
    },
  };
};
